/**
 * The SKU grid's "Filters": the AP's rule builder
 * (``DagFilterExpressionBuilder``) over SKU fields, evaluated in the browser.
 */
import { isFilterGroup, type FilterCondition, type FilterGroup } from '@/types'
import type { FieldMeta } from '@/components/PlanDetail/dagFilterUtils'
import type { SkuType, SkuWaveRow } from './sku-model'

const SKU_TYPES: SkuType[] = ['Single SKU', 'Parent', 'Child', 'Replen']

/** Filterable fields, in the order the builder lists them. */
export const SKU_FILTER_FIELDS: Record<string, FieldMeta> = {
  variancePercent: { label: 'Var %', widget: 'number' },
  varianceUnits: { label: 'Var vs AP', widget: 'number' },
  wpQty: { label: 'Total Wave Quantity', widget: 'number' },
  apQty: { label: 'AP Qty', widget: 'number' },
  stores: { label: 'Stores', widget: 'number' },
  sku: { label: 'SKU', widget: 'text' },
  description: { label: 'Description', widget: 'text' },
  vendor: { label: 'Vendor', widget: 'text' },
  type: {
    label: 'Type',
    widget: 'dropdown',
    options: SKU_TYPES.map(type => ({ value: type, label: type })),
  },
  needsReview: { label: 'Needs review', widget: 'boolean' },
}

export const SKU_FILTER_ORDER = Object.keys(SKU_FILTER_FIELDS)

/** A row's value for a filter field. Var % is in percent (the row holds a fraction). */
function skuFieldValue(
  row: SkuWaveRow,
  field: string
): string | number | boolean | null {
  const line = row.line
  switch (field) {
    case 'variancePercent':
      return row.variancePercent == null ? null : row.variancePercent * 100
    case 'varianceUnits':
      return row.varianceUnits ?? null
    case 'wpQty':
      return row.wpQty ?? null
    case 'apQty':
      return row.totalQty
    case 'stores':
      return line?.stores ?? null
    case 'sku':
      return line?.sku ?? null
    case 'description':
      return line?.description ?? null
    case 'vendor':
      return line?.vendor ?? null
    case 'type':
      return line?.type ?? null
    case 'needsReview':
      return row.needsReview
    default:
      return null
  }
}

const EPSILON = 1e-9

function matchesCondition(
  row: SkuWaveRow,
  condition: FilterCondition
): boolean {
  const meta = SKU_FILTER_FIELDS[condition.field]
  if (!meta) return true
  const actual = skuFieldValue(row, condition.field)
  const expected = condition.value
  if (meta.widget === 'number') {
    const target = Number(expected)
    // An empty or non-numeric value doesn't restrict anything yet.
    if (String(expected).trim() === '' || Number.isNaN(target)) return true
    if (actual === null || typeof actual !== 'number') return false
    switch (condition.operator) {
      case 'eq':
        return Math.abs(actual - target) < EPSILON
      case 'neq':
        return Math.abs(actual - target) >= EPSILON
      case 'lt':
        return actual < target - EPSILON
      case 'lte':
        return actual <= target + EPSILON
      case 'gt':
        return actual > target + EPSILON
      case 'gte':
        return actual >= target - EPSILON
      default:
        return true
    }
  }
  if (meta.widget === 'boolean') {
    // The builder edits "true"/"false"; applied filters hold booleans.
    if (![true, false, 'true', 'false'].includes(expected as never)) return true
    const value = expected === true || expected === 'true'
    return condition.operator === 'neq' ? actual !== value : actual === value
  }
  if (condition.operator === 'in') {
    const values = Array.isArray(expected) ? expected : []
    return !values.length || values.includes(String(actual ?? ''))
  }
  const text = String(actual ?? '').toLowerCase()
  const wanted = String(expected ?? '').toLowerCase()
  if (!wanted) return true
  switch (condition.operator) {
    case 'eq':
      return text === wanted
    case 'neq':
      return text !== wanted
    case 'contains':
      return text.includes(wanted)
    case 'starts_with':
      return text.startsWith(wanted)
    default:
      return true
  }
}

/** Whether a row passes the filter (AND/OR groups, nested). No filter: every row. */
export function matchesSkuFilter(
  row: SkuWaveRow,
  group: FilterGroup | null
): boolean {
  if (!group || !group.conditions.length) return true
  const results = group.conditions.map(item =>
    isFilterGroup(item)
      ? matchesSkuFilter(row, item)
      : matchesCondition(row, item)
  )
  return group.op === 'OR' ? results.some(Boolean) : results.every(Boolean)
}

/**
 * An applied filter as the builder edits it: applied booleans are booleans,
 * the builder's Yes/No select edits "true"/"false" (re-applying the boolean as
 * is would read as "false").
 */
export function skuFilterDraft(group: FilterGroup): FilterGroup {
  return {
    ...group,
    conditions: group.conditions.map(item =>
      isFilterGroup(item)
        ? skuFilterDraft(item)
        : typeof item.value === 'boolean'
          ? { ...item, value: String(item.value) }
          : item
    ),
  }
}
